import { AlertTriangle, BellOff, CheckCircle2, Clock, ShieldQuestion } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fmtDate } from '@/shared/lib/format'
import type {
  RiskBandCode, SupportBandCode, VerificationStatusCode, VoterResponse,
} from '@/shared/api/types'
import {
  CONFIDENCE_LABEL, SUPPRESSION_LABEL, SUPPORT_BAND_LABEL, VERIFICATION_LABEL,
} from './api'

/**
 * The intelligence columns of the voter row.
 *
 * The rule that matters here is the same one `geography/IntelCells.tsx` learned
 * the hard way: **grey means "not assessed", never "zero"**. A voter with
 * `supportLevel: 0` and `supportConfidence: UNASSESSED` has not been asked; a
 * voter with `supportLevel: 0` and `REPORTED` is an opponent. Rendering both as
 * an empty bar would erase the only distinction the field team cares about.
 */

const BAND_COLOUR: Record<SupportBandCode, string> = {
  OPPOSED: 'bg-red-500',
  LEAN_OPPOSED: 'bg-orange-400',
  UNDECIDED: 'bg-amber-400',
  LEAN_SUPPORT: 'bg-lime-500',
  SUPPORTER: 'bg-emerald-500',
  UNASSESSED: 'bg-slate-300 dark:bg-slate-600',
}

export function isUnassessed(v: VoterResponse): boolean {
  return v.confidenceCode === 'UNASSESSED' || v.supportBandCode === 'UNASSESSED'
}

export function SupportCell({ voter }: { voter: VoterResponse }) {
  if (isUnassessed(voter)) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-erp-xs text-muted-foreground"
        title="Never assessed — not the same as a support level of zero"
      >
        <ShieldQuestion className="h-3 w-3" />
        Unassessed
      </span>
    )
  }
  const pct = Math.max(0, Math.min(100, voter.supportLevel))
  return (
    <span
      className="inline-flex items-center gap-2 min-w-0"
      title={`${pct}% · ${SUPPORT_BAND_LABEL[voter.supportBandCode] ?? voter.supportBandCode} · ${
        CONFIDENCE_LABEL[voter.confidenceCode] ?? voter.confidenceCode}`}
    >
      <span className="h-1.5 w-14 rounded-full bg-muted overflow-hidden shrink-0 axis-hide-md">
        <span className={cn('block h-full rounded-full', BAND_COLOUR[voter.supportBandCode])} style={{ width: `${pct}%` }} />
      </span>
      <span className="erp-metric text-erp-sm tabular-nums">{pct}</span>
      {voter.confidenceCode === 'VERIFIED' && (
        <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" aria-label="Verified" />
      )}
    </span>
  )
}

const RISK_STYLE: Record<RiskBandCode, string> = {
  LOW: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  MEDIUM: 'bg-amber-50 text-amber-700 border-amber-200',
  HIGH: 'bg-orange-50 text-orange-700 border-orange-200',
  CRITICAL: 'bg-red-50 text-red-700 border-red-200',
}

export function RiskCell({ voter }: { voter: VoterResponse }) {
  const band = voter.overlay?.riskBandCode
  if (!band) return <span className="text-muted-foreground">—</span>
  return (
    <span className={cn(
      'inline-flex items-center px-1.5 py-0.5 text-erp-xs font-medium border rounded-md capitalize',
      RISK_STYLE[band],
    )}>
      {band.toLowerCase()}
    </span>
  )
}

/** Item count plus open alerts — the "is there a file on this person" column. */
export function IntelCell({ voter }: { voter: VoterResponse }) {
  const o = voter.overlay
  if (!o || o.itemCount === 0) return <span className="text-muted-foreground">—</span>
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="erp-metric text-erp-sm tabular-nums">{o.itemCount}</span>
      {o.openAlertCount > 0 && (
        <span
          className="inline-flex items-center gap-0.5 text-erp-xs text-red-700 dark:text-red-400"
          title={`${o.openAlertCount} open alert${o.openAlertCount === 1 ? '' : 's'}`}
        >
          <AlertTriangle className="h-3 w-3" />
          {o.openAlertCount}
        </span>
      )}
      {o.credibilityGrade && (
        <span
          className="text-erp-xs font-mono text-muted-foreground axis-hide-lg"
          title="Admiralty grade of the best-graded item: source reliability A–F, information credibility 1–6"
        >
          {o.credibilityGrade}
        </span>
      )}
    </span>
  )
}

const VERIFY_STYLE: Record<VerificationStatusCode, string> = {
  UNVERIFIED: 'text-muted-foreground',
  PHONE_VERIFIED: 'text-sky-700 dark:text-sky-400',
  FIELD_VERIFIED: 'text-emerald-700 dark:text-emerald-400',
  ID_SIGHTED: 'text-emerald-700 dark:text-emerald-400',
  IEBC_MATCHED: 'text-emerald-800 dark:text-emerald-300 font-medium',
  FAILED: 'text-red-700 dark:text-red-400',
}

export function VerificationCell({ status }: { status: VerificationStatusCode }) {
  return (
    <span className={cn('text-erp-xs', VERIFY_STYLE[status])}>
      {VERIFICATION_LABEL[status] ?? status}
    </span>
  )
}

/**
 * Contactability.
 *
 * The list row deliberately shows **only** the suppression flag, because
 * `VoterResponse` carries only that. `lastContactedAt` and `contactCount` are on
 * `VoterDetailResponse`, so "when did we last speak" is a drawer question, not a
 * column — and inventing the column here would mean an extra request per row.
 *
 * Suppression is on the row precisely because it is the one fact a caller needs
 * *before* acting on it. Every outbound path must consult it.
 */
export function ContactCell({ voter }: { voter: VoterResponse }) {
  if (!voter.doNotContact) {
    return <span className="text-erp-xs text-muted-foreground">Contactable</span>
  }
  const why = voter.doNotContactReasonCode
    ? SUPPRESSION_LABEL[voter.doNotContactReasonCode] ?? voter.doNotContactReasonCode
    : null
  return (
    <span
      className="inline-flex items-center gap-1 text-erp-xs text-red-700 dark:text-red-400"
      title={why ? `Do not contact — ${why}` : 'Do not contact'}
    >
      <BellOff className="h-3 w-3" />
      Suppressed
    </span>
  )
}

/**
 * Whether we actually got this person moved — or registered.
 *
 * The question the registry could not answer before: *"which of these did we manage to
 * get transferred?"* A polling station does not answer it; a station is where they are,
 * not evidence that anyone moved them there.
 *
 * Two independent facts, read in this order:
 *
 * 1. **Recorded evidence** (`lastTransfer*`) — lodged with the IEBC, confirmed by the
 *    IEBC, written to our register. It is sticky: a later request that gets withdrawn
 *    does not un-relocate anybody, so the evidence outranks the work in progress.
 *    A recorded REGISTRATION reads "Registered", not "Relocated" — putting someone ON
 *    the roll is a different sentence from moving them, and merging the two would
 *    over-report the number this campaign is judged on.
 * 2. **Work in flight** (`openTransfer*`) — shown when there is no evidence yet, because
 *    it is why a second request for this person is refused, and a field officer who sees
 *    it does not waste a visit.
 *
 * Nothing renders when both are absent, and nothing renders when the backend does not
 * send the fields at all — an empty cell is honest; "not relocated" would not be.
 */
export function TransferCell({ voter }: { voter: VoterResponse }) {
  if (voter.lastTransferRecordedAt) {
    const registered = voter.lastTransferKind === 'REGISTRATION'
    return (
      <span
        className="inline-flex items-center gap-1.5 text-erp-xs"
        title={[voter.lastTransferReference, `recorded ${fmtDate(voter.lastTransferRecordedAt)}`]
          .filter(Boolean).join(' · ')}
      >
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
        <span className="text-emerald-700 dark:text-emerald-400">
          {registered ? 'Registered' : 'Relocated'}
        </span>
      </span>
    )
  }

  if (voter.openTransferReference) {
    return (
      <span className="inline-flex items-center gap-1.5 text-erp-xs" title={voter.openTransferReference}>
        <Clock className="h-3.5 w-3.5 text-amber-600 shrink-0" />
        <span className="text-amber-700 dark:text-amber-400">
          {voter.openTransferKind === 'REGISTRATION' ? 'Registration open' : 'Transfer open'}
        </span>
      </span>
    )
  }

  return <span className="text-muted-foreground/50">—</span>
}
